import { RevConnectInfoManager } from './RevConnectInfoManager';

interface IFragmentEntry {
	fragmentBucket: string;
	fragmentBytesOrigin: number;
	fragmentBytesPeers: number;
}

interface IStatsReport {
	playlistActualBytes: number;
	playlistEstimatedNetworkBytes: number;
	signalingActualBytes: number;
	signalingEstimatedNetworkBytes: number;
	fragmentDetails: IFragmentEntry[];
	fragmentOriginFetchFilenames: string[];
	totalFragmentBytesOrigin: number;
	totalFragmentBytesPeers: number;
}

export class RevConnectStatsManager {
	private fragmentOriginFilenames: string[];
	private fragmentStats: IFragmentEntry[];
	private playlistBytesOrigin: number;
	private playlistEstimatedNetworkBytes: number;
	private signalingBytes: number;
	private signalingEstimatedNetworkBytes: number;

	constructor() {
		this.playlistBytesOrigin = 0;
		this.playlistEstimatedNetworkBytes = 0;
		this.signalingBytes = 0;
		this.signalingEstimatedNetworkBytes = 0;
		this.fragmentStats = []; // fragment stats are collected into buckets for each type we receive (e.g. content_low, speaker_high, etc)
		this.fragmentOriginFilenames = []; // used to keep track of all fragments fetched from origin until cleared (which would normally happen when reported)
	}

	public clearIntervalStats(): void {
		this.fragmentOriginFilenames = [];
	}

	public getStatsReport(): IStatsReport {
		let statsReport: IStatsReport = {
			playlistActualBytes: this.playlistBytesOrigin,
			playlistEstimatedNetworkBytes: this.playlistEstimatedNetworkBytes,
			signalingActualBytes: this.signalingBytes,
			signalingEstimatedNetworkBytes: this.signalingEstimatedNetworkBytes,
			fragmentDetails: this.fragmentStats,
			fragmentOriginFetchFilenames: this.fragmentOriginFilenames,
			totalFragmentBytesOrigin: 0,
			totalFragmentBytesPeers: 0
		}
		this.fragmentStats.forEach(statEntry => {
			statsReport.totalFragmentBytesOrigin += statEntry.fragmentBytesOrigin;
			statsReport.totalFragmentBytesPeers += statEntry.fragmentBytesPeers;
		})

		return statsReport;
	}

	public recordStatsFragmentOrigin(url: string, size: number): void {
		const fileName: string = url.split('/').pop().split('?')[0];
		const lastUnderscorePos: number = fileName.lastIndexOf('_');
		const prefix: string = (lastUnderscorePos > 0) ? fileName.substring(0, lastUnderscorePos) : fileName;

		let fragmentEntry: IFragmentEntry = this.fragmentStats.find(element => element.fragmentBucket == prefix);
		if (fragmentEntry) {
			fragmentEntry.fragmentBytesOrigin += size;
		}
		else {
			fragmentEntry = {
				fragmentBucket: prefix,
				fragmentBytesOrigin: size,
				fragmentBytesPeers: 0,
			}
			this.fragmentStats.push(fragmentEntry);
		}

		// if we are not sending stats to signaling, then we do not want to track origin filenames because nothing will clear them
		if (RevConnectInfoManager.getRevConnectConfig().enableStatsToSignaling) {
			this.fragmentOriginFilenames.push(fileName);
		}
	}

	public recordStatsFragmentPeer(url: string, size: number): void {
		const fileName: string = url.split('/').pop().split('?')[0];				
		const lastUnderscorePos: number = fileName.lastIndexOf('_');
		const prefix: string = (lastUnderscorePos > 0) ? fileName.substring(0, lastUnderscorePos) : fileName;

		let fragmentEntry: IFragmentEntry = this.fragmentStats.find(element => element.fragmentBucket == prefix);
		if (fragmentEntry) {
			fragmentEntry.fragmentBytesPeers += size;
		}
		else {
			fragmentEntry = {
				fragmentBucket: prefix,
				fragmentBytesOrigin: 0,
				fragmentBytesPeers: size,
			}
			this.fragmentStats.push(fragmentEntry);
		}
	}

	public recordStatsPlaylist(url: string, size: number): void {
		const fileName: string = url.split('/').pop().split('?')[0];				
		this.playlistBytesOrigin += size;
		this.playlistEstimatedNetworkBytes += Math.round(RevConnectInfoManager.getRevConnectConfig().playlistByteEstimateMultiplier * size);
	}

	public recordStatsSignaling(size: number): void {
		this.signalingBytes += size;
		this.signalingEstimatedNetworkBytes += Math.round(RevConnectInfoManager.getRevConnectConfig().signalingByteEstimateMultiplier * size);
	}

}

export const RevConnectStatsManagerInstance = new RevConnectStatsManager();
